import React, { useState, useCallback, useEffect } from 'react';
import ReactFlow, { 
  MiniMap, 
  Controls, 
  Background, 
  ReactFlowProvider,
  applyNodeChanges,
  applyEdgeChanges
} from 'reactflow';
import 'reactflow/dist/style.css';
import { themeStyles, customStyles } from './ArtemisStyles';
import { nodeTypes } from './ArtemisCustomNodes';

// Memory Optimization nodes - showing evolution of memory usage and garbage collection
const getInitialNodes = () => {
  const savedPositions = localStorage.getItem('artemisMemoryOptimizationPositions');
  if (savedPositions) {
    return JSON.parse(savedPositions);
  }
  
  return [
    // User Intent
    {
      id: 'user-intent',
      data: { label: '💬 "Fix these memory leaks and optimize garbage collection"' },
      position: { x: 400, y: 50 },
      style: themeStyles.problemNode,
    },
    
    // Memory Analysis
    {
      id: 'memory-analysis',
      data: { label: '🧠 Memory Profiling\n• Heap usage: 850MB\n• Memory leaks: 7 detected\n• GC pressure: High\n• OOM crashes: 12/day' },
      position: { x: 800, y: 50 },
      style: { ...themeStyles.analysisNode, width: 220 },
    },
    
    // Initial Memory-Heavy Code
    {
      id: 'initial-code',
      type: 'collapsibleCode',
      data: { 
        filename: 'memory-heavy.js',
        code: '// Memory-inefficient data processing\n' +
              'class DataProcessor {\n' +
              '  constructor() {\n' +
              '    this.cache = new Map(); // Never cleared\n' +
              '    this.eventListeners = [];\n' +
              '    this.timers = [];\n' +
              '  }\n' +
              '  \n' +
              '  async processLargeDataset(data) {\n' +
              '    // Memory leak: creating massive arrays\n' +
              '    const results = [];\n' +
              '    const intermediate = [];\n' +
              '    \n' +
              '    for (let i = 0; i < data.length; i++) {\n' +
              '      // Inefficient object creation\n' +
              '      const processed = {\n' +
              '        ...data[i],\n' +
              '        timestamp: new Date(),\n' +
              '        metadata: this.generateMetadata(data[i]),\n' +
              '        history: this.getFullHistory(data[i].id)\n' +
              '      };\n' +
              '      \n' +
              '      results.push(processed);\n' +
              '      intermediate.push(this.heavyComputation(processed));\n' +
              '      \n' +
              '      // Memory leak: accumulating in cache\n' +
              '      this.cache.set(data[i].id, processed);\n' +
              '    }\n' +
              '    \n' +
              '    return { results, intermediate };\n' +
              '  }\n' +
              '}',
        issues: 'Memory leaks, inefficient object creation, no cleanup, unbounded cache',
        score: 1.2
      },
      position: { x: 400, y: 200 },
    },
    
    // Scoring System
    {
      id: 'scoring-label',
      data: { label: '💾 Memory Performance Metrics' },
      position: { x: 1100, y: 200 },
      style: { ...themeStyles.metricNode, width: 240 },
    },
    {
      id: 'heap-score',
      data: { label: '📊 Heap Usage\n850MB peak\nTarget: <200MB' },
      position: { x: 950, y: 280 },
      style: themeStyles.metricNode,
    },
    {
      id: 'gc-score',
      data: { label: '♻️ GC Efficiency\nScore: 2/10\nFrequent long pauses' },
      position: { x: 1100, y: 280 },
      style: themeStyles.metricNode,
    },
    {
      id: 'leak-score',
      data: { label: '🚰 Memory Leaks\n7 active leaks\nGrowing over time' },
      position: { x: 1250, y: 280 },
      style: themeStyles.metricNode,
    },
    {
      id: 'stability-tests',
      data: { label: '⚡ Stability Tests\n12 OOM crashes/day\n❌ Fails long-running tests' },
      position: { x: 1025, y: 380 },
      style: themeStyles.suggestionNode,
    },
    {
      id: 'performance-impact',
      data: { label: '🐌 Performance Impact\nGC pauses: 200ms+\nApp freezing' },
      position: { x: 1175, y: 380 },
      style: themeStyles.suggestionNode,
    },
    
    // Genetic Algorithm Engine
    {
      id: 'genetic-algo',
      data: { label: '🧬 Memory Evolution Engine\n\n🔄 Mutation: Optimize allocations & cleanup\n🔗 Crossover: Combine memory patterns\n📈 Selection: Minimize memory + maximize performance\n🎲 Population: 18 optimized variants' },
      position: { x: 100, y: 500 },
      style: { ...themeStyles.uniqueFeatureNode, width: 300, height: 140 },
    },
    
    // AI Agents with Prompts
    {
      id: 'ai-agents',
      data: { label: '🤖 Memory Optimization Specialists' },
      position: { x: 500, y: 500 },
      style: { ...themeStyles.analysisNode, width: 260 },
    },
    {
      id: 'leak-agent',
      data: { label: '🚰 Leak Prevention Agent\n\n📝 Prompt:\n"Identify and fix memory leaks.\nImplement proper cleanup in\ndestructors, remove event listeners,\nclear timers, and manage\ncache size limits."' },
      position: { x: 200, y: 600 },
      style: { ...themeStyles.llmNode, width: 220, height: 160 },
    },
    {
      id: 'gc-agent',
      data: { label: '♻️ GC Optimization Agent\n\n📝 Prompt:\n"Optimize garbage collection:\nreduce object allocations,\nuse object pooling, implement\ngenerational-friendly patterns,\nand minimize GC pressure."' },
      position: { x: 500, y: 600 },
      style: { ...themeStyles.llmNode, width: 220, height: 160 },
    },
    {
      id: 'allocation-agent',
      data: { label: '💾 Allocation Agent\n\n📝 Prompt:\n"Optimize memory allocation:\nuse pre-allocated buffers,\nimplement memory pools,\nreduce object creation,\nand stream large datasets."' },
      position: { x: 800, y: 600 },
      style: { ...themeStyles.llmNode, width: 220, height: 160 },
    },
    
    // Generation 1 Label
    {
      id: 'gen1-label',
      data: { label: '🌱 Generation 1: Initial Memory Optimizations (Population: 18)' },
      position: { x: 500, y: 800 },
      style: { ...themeStyles.metricNode, width: 450 },
    },
    
    // Generation 1 Variants
    {
      id: 'gen1-1',
      type: 'collapsibleCode',
      data: { 
        filename: 'memory-v1a.js (Leak Prevention)',
        code: '// Memory leak prevention variant\n' +
              '+ class OptimizedDataProcessor {\n' +
              '+   constructor() {\n' +
              '+     // LRU cache with size limit\n' +
              '+     this.cache = new Map();\n' +
              '+     this.maxCacheSize = 1000;\n' +
              '+     this.eventListeners = new Set();\n' +
              '+     this.timers = new Set();\n' +
              '+   }\n' +
              '+   \n' +
              '+   async processLargeDataset(data) {\n' +
              '+     try {\n' +
              '+       const results = [];\n' +
              '+       \n' +
              '+       for (let i = 0; i < data.length; i++) {\n' +
              '+         const processed = this.processItem(data[i]);\n' +
              '+         results.push(processed);\n' +
              '+         \n' +
              '+         // Implement LRU cache eviction\n' +
              '+         this.addToCache(data[i].id, processed);\n' +
              '+         \n' +
              '+         // Periodic cleanup to prevent memory buildup\n' +
              '+         if (i % 1000 === 0) {\n' +
              '+           this.performMaintenance();\n' +
              '+         }\n' +
              '+       }\n' +
              '+       \n' +
              '+       return results;\n' +
              '+     } finally {\n' +
              '+       // Ensure cleanup even if exception occurs\n' +
              '+       this.cleanup();\n' +
              '+     }\n' +
              '+   }\n' +
              '+   \n' +
              '+   addToCache(key, value) {\n' +
              '+     if (this.cache.size >= this.maxCacheSize) {\n' +
              '+       const firstKey = this.cache.keys().next().value;\n' +
              '+       this.cache.delete(firstKey);\n' +
              '+     }\n' +
              '+     this.cache.set(key, value);\n' +
              '+   }\n' +
              '+   \n' +
              '+   cleanup() {\n' +
              '+     this.clearEventListeners();\n' +
              '+     this.clearTimers();\n' +
              '+     this.cache.clear();\n' +
              '+   }\n' +
              '+ }',
        score: 3.6
      },
      position: { x: 50, y: 900 },
    },
    {
      id: 'gen1-2',
      type: 'collapsibleCode',
      data: { 
        filename: 'memory-v1b.js (GC Optimization)',
        code: '// Garbage collection optimization variant\n' +
              '+ class GCOptimizedProcessor {\n' +
              '+   constructor() {\n' +
              '+     // Pre-allocate object pools\n' +
              '+     this.objectPool = new ObjectPool(100);\n' +
              '+     this.bufferPool = new BufferPool(50, 1024);\n' +
              '+   }\n' +
              '+   \n' +
              '+   async processLargeDataset(data) {\n' +
              '+     const batchSize = 100;\n' +
              '+     const results = [];\n' +
              '+     \n' +
              '+     // Process in batches to reduce GC pressure\n' +
              '+     for (let i = 0; i < data.length; i += batchSize) {\n' +
              '+       const batch = data.slice(i, i + batchSize);\n' +
              '+       const batchResults = await this.processBatch(batch);\n' +
              '+       results.push(...batchResults);\n' +
              '+       \n' +
              '+       // Force minor GC between batches if needed\n' +
              '+       if (process.memoryUsage().heapUsed > 100 * 1024 * 1024) {\n' +
              '+         await this.yieldToGC();\n' +
              '+       }\n' +
              '+     }\n' +
              '+     \n' +
              '+     return results;\n' +
              '+   }\n' +
              '+   \n' +
              '+   async processBatch(batch) {\n' +
              '+     const results = [];\n' +
              '+     \n' +
              '+     for (const item of batch) {\n' +
              '+       // Reuse objects from pool\n' +
              '+       const processedObj = this.objectPool.acquire();\n' +
              '+       processedObj.reset(item);\n' +
              '+       \n' +
              '+       const result = this.processItem(processedObj);\n' +
              '+       results.push(result);\n' +
              '+       \n' +
              '+       // Return to pool for reuse\n' +
              '+       this.objectPool.release(processedObj);\n' +
              '+     }\n' +
              '+     \n' +
              '+     return results;\n' +
              '+   }\n' +
              '+   \n' +
              '+   async yieldToGC() {\n' +
              '+     return new Promise(resolve => setImmediate(resolve));\n' +
              '+   }\n' +
              '+ }',
        score: 4.0
      },
      position: { x: 400, y: 900 },
    },
    {
      id: 'gen1-3',
      type: 'collapsibleCode',
      data: { 
        filename: 'memory-v1c.js (Streaming Approach)',
        code: '// Streaming memory optimization variant\n' +
              '+ const { pipeline, Transform } = require("stream");\n' +
              '+ const { promisify } = require("util");\n' +
              '+ const pipelineAsync = promisify(pipeline);\n' +
              '+\n' +
              '+ class StreamingProcessor {\n' +
              '+   constructor() {\n' +
              '+     this.memoryThreshold = 50 * 1024 * 1024; // 50MB\n' +
              '+   }\n' +
              '+   \n' +
              '+   async processLargeDataset(dataStream) {\n' +
              '+     const results = [];\n' +
              '+     \n' +
              '+     // Create processing pipeline\n' +
              '+     const processor = new Transform({\n' +
              '+       objectMode: true,\n' +
              '+       highWaterMark: 16, // Limit buffer size\n' +
              '+       transform(chunk, encoding, callback) {\n' +
              '+         try {\n' +
              '+           // Process item with minimal memory footprint\n' +
              '+           const processed = {\n' +
              '+             id: chunk.id,\n' +
              '+             result: this.computeResult(chunk),\n' +
              '+             timestamp: Date.now()\n' +
              '+           };\n' +
              '+           \n' +
              '+           callback(null, processed);\n' +
              '+         } catch (error) {\n' +
              '+           callback(error);\n' +
              '+         }\n' +
              '+       }\n' +
              '+     });\n' +
              '+     \n' +
              '+     const collector = new Transform({\n' +
              '+       objectMode: true,\n' +
              '+       transform(chunk, encoding, callback) {\n' +
              '+         results.push(chunk);\n' +
              '+         \n' +
              '+         // Monitor memory usage\n' +
              '+         if (process.memoryUsage().heapUsed > this.memoryThreshold) {\n' +
              '+           this.flushToDisk(results);\n' +
              '+           results.length = 0; // Clear array\n' +
              '+         }\n' +
              '+         \n' +
              '+         callback();\n' +
              '+       }\n' +
              '+     });\n' +
              '+     \n' +
              '+     await pipelineAsync(dataStream, processor, collector);\n' +
              '+     return results;\n' +
              '+   }\n' +
              '+ }',
        score: 4.2
      },
      position: { x: 750, y: 900 },
    },
    {
      id: 'gen1-more',
      data: { label: '... 15 more variants\nwith different strategies' },
      position: { x: 950, y: 920 },
      style: { ...themeStyles.suggestionNode, width: 150 },
    },
    
    // Evolution Process
    {
      id: 'evolution-1',
      type: 'evolution',
      data: { 
        generation: '1→2',
        description: '🔄 Memory Pattern Fusion',
        scores: {
          'Heap Usage': '850MB → 180MB',
          'GC Efficiency': '2 → 7',
          'Memory Leaks': '7 → 1',
          'Stability': '12 crashes → 1'
        }
      },
      position: { x: 400, y: 1100 },
    },
    
    // Generation 2 Label
    {
      id: 'gen2-label',
      data: { label: '🧬 Generation 2: Memory-Efficient Architecture (Advanced Patterns)' },
      position: { x: 500, y: 1250 },
      style: { ...themeStyles.metricNode, width: 500 },
    },
    
    // Generation 2 - Combined Memory Optimizations
    {
      id: 'gen2-1',
      type: 'collapsibleCode',
      data: { 
        filename: 'memory-elite.js (Elite Memory Management)',
        code: '// Ultra-efficient memory management system\n' +
              '+ import { MemoryManager } from "./memory/MemoryManager";\n' +
              '+ import { ObjectPool } from "./pools/ObjectPool";\n' +
              '+ import { StreamProcessor } from "./streams/StreamProcessor";\n' +
              '+ import { GCOptimizer } from "./gc/GCOptimizer";\n' +
              '+\n' +
              '+ class EliteDataProcessor {\n' +
              '+   constructor(options = {}) {\n' +
              '+     this.memoryManager = new MemoryManager({\n' +
              '+       maxHeapSize: options.maxHeapSize || 100 * 1024 * 1024, // 100MB\n' +
              '+       gcThreshold: options.gcThreshold || 0.8\n' +
              '+     });\n' +
              '+     \n' +
              '+     this.objectPool = new ObjectPool({\n' +
              '+       factory: () => new ProcessedItem(),\n' +
              '+       reset: (item) => item.reset(),\n' +
              '+       maxSize: 1000\n' +
              '+     });\n' +
              '+     \n' +
              '+     this.streamProcessor = new StreamProcessor({\n' +
              '+       highWaterMark: 16,\n' +
              '+       objectMode: true\n' +
              '+     });\n' +
              '+     \n' +
              '+     this.gcOptimizer = new GCOptimizer();\n' +
              '+   }\n' +
              '+\n' +
              '+   async processLargeDataset(dataSource) {\n' +
              '+     const memoryStats = { peak: 0, average: 0, samples: 0 };\n' +
              '+     \n' +
              '+     try {\n' +
              '+       // Monitor memory usage throughout processing\n' +
              '+       const memoryMonitor = this.memoryManager.startMonitoring();\n' +
              '+       \n' +
              '+       // Use streaming approach for large datasets\n' +
              '+       const results = await this.streamProcessor.process(dataSource, {\n' +
              '+         transform: async (item) => {\n' +
              '+           // Get object from pool\n' +
              '+           const processed = this.objectPool.acquire();\n' +
              '+           \n' +
              '+           try {\n' +
              '+             // Process with minimal allocations\n' +
              '+             processed.initialize(item);\n' +
              '+             const result = await this.optimizedProcessing(processed);\n' +
              '+             \n' +
              '+             // Update memory stats\n' +
              '+             const currentMemory = process.memoryUsage().heapUsed;\n' +
              '+             memoryStats.peak = Math.max(memoryStats.peak, currentMemory);\n' +
              '+             memoryStats.average = (memoryStats.average * memoryStats.samples + currentMemory) / (memoryStats.samples + 1);\n' +
              '+             memoryStats.samples++;\n' +
              '+             \n' +
              '+             return result;\n' +
              '+           } finally {\n' +
              '+             // Always return to pool\n' +
              '+             this.objectPool.release(processed);\n' +
              '+           }\n' +
              '+         },\n' +
              '+         \n' +
              '+         onBatch: async (batchResults) => {\n' +
              '+           // Proactive GC management\n' +
              '+           if (this.memoryManager.shouldTriggerGC()) {\n' +
              '+             await this.gcOptimizer.optimizedGC();\n' +
              '+           }\n' +
              '+         }\n' +
              '+       });\n' +
              '+       \n' +
              '+       memoryMonitor.stop();\n' +
              '+       return { results, memoryStats };\n' +
              '+     } catch (error) {\n' +
              '+       await this.emergencyCleanup();\n' +
              '+       throw error;\n' +
              '+     }\n' +
              '+   }\n' +
              '+ }',
        score: 4.8
      },
      position: { x: 200, y: 1350 },
    },
    {
      id: 'gen2-2',
      type: 'collapsibleCode',
      data: { 
        filename: 'memory-manager.js (Memory Control)',
        code: '// Advanced memory management system\n' +
              '+ export class MemoryManager {\n' +
              '+   constructor(options) {\n' +
              '+     this.maxHeapSize = options.maxHeapSize;\n' +
              '+     this.gcThreshold = options.gcThreshold;\n' +
              '+     this.monitoring = false;\n' +
              '+     this.metrics = {\n' +
              '+       allocations: 0,\n' +
              '+       deallocations: 0,\n' +
              '+       gcTriggers: 0,\n' +
              '+       peakUsage: 0\n' +
              '+     };\n' +
              '+   }\n' +
              '+\n' +
              '+   startMonitoring() {\n' +
              '+     this.monitoring = true;\n' +
              '+     const interval = setInterval(() => {\n' +
              '+       if (!this.monitoring) {\n' +
              '+         clearInterval(interval);\n' +
              '+         return;\n' +
              '+       }\n' +
              '+       \n' +
              '+       const memUsage = process.memoryUsage();\n' +
              '+       this.metrics.peakUsage = Math.max(this.metrics.peakUsage, memUsage.heapUsed);\n' +
              '+       \n' +
              '+       // Predict memory pressure\n' +
              '+       if (this.predictMemoryPressure(memUsage)) {\n' +
              '+         this.proactiveCleanup();\n' +
              '+       }\n' +
              '+     }, 100);\n' +
              '+     \n' +
              '+     return {\n' +
              '+       stop: () => {\n' +
              '+         this.monitoring = false;\n' +
              '+         clearInterval(interval);\n' +
              '+       }\n' +
              '+     };\n' +
              '+   }\n' +
              '+\n' +
              '+   shouldTriggerGC() {\n' +
              '+     const memUsage = process.memoryUsage();\n' +
              '+     const heapUtilization = memUsage.heapUsed / this.maxHeapSize;\n' +
              '+     return heapUtilization > this.gcThreshold;\n' +
              '+   }\n' +
              '+\n' +
              '+   predictMemoryPressure(memUsage) {\n' +
              '+     // Simple prediction based on heap growth rate\n' +
              '+     const projectedUsage = memUsage.heapUsed * 1.2; // 20% safety margin\n' +
              '+     return projectedUsage > this.maxHeapSize * this.gcThreshold;\n' +
              '+   }\n' +
              '+\n' +
              '+   async proactiveCleanup() {\n' +
              '+     this.metrics.gcTriggers++;\n' +
              '+     // Trigger incremental GC\n' +
              '+     if (global.gc) {\n' +
              '+       global.gc();\n' +
              '+     }\n' +
              '+   }\n' +
              '+ }',
        score: 4.6
      },
      position: { x: 600, y: 1350 },
    },
    
    // Final Evolution
    {
      id: 'evolution-2',
      type: 'evolution',
      data: { 
        generation: '2→Final',
        description: '🏆 Memory-Optimized Perfection',
        scores: {
          'Heap Usage': '180MB → 45MB',
          'GC Efficiency': '7 → 10',
          'Memory Leaks': '1 → 0',
          'Stability': '0 crashes'
        }
      },
      position: { x: 400, y: 1550 },
    },
    
    // Final Output
    {
      id: 'final-output',
      type: 'collapsibleCode',
      data: { 
        filename: 'Enterprise Memory System',
        code: '// Final memory-optimized architecture\n' +
              '// ✅ 95% memory usage reduction (850MB → 45MB)\n' +
              '// ✅ Zero memory leaks detected\n' +
              '// ✅ GC pauses under 5ms\n' +
              '// ✅ Zero out-of-memory crashes\n' +
              '// ✅ Predictive memory management\n' +
              '// ✅ Real-time memory monitoring\n' +
              '// ✅ Automatic memory optimization\n' +
              '// ✅ Streaming large dataset processing\n' +
              '\n' +
              '💾 Memory Architecture:\n' +
              '├── Intelligent object pooling\n' +
              '├── LRU caches with size limits\n' +
              '├── Streaming data processing\n' +
              '├── Proactive garbage collection\n' +
              '├── Memory leak detection & prevention\n' +
              '├── Predictive memory pressure management\n' +
              '└── Real-time memory monitoring & alerting\n' +
              '\n' +
              '// Memory improvements:\n' +
              '// • 95% heap usage reduction (850MB → 45MB)\n' +
              '// • 100% memory leak elimination\n' +
              '// • 98% GC pause time reduction (200ms → 5ms)\n' +
              '// • 100% crash elimination (12/day → 0)\n' +
              '// • 400% processing throughput increase',
        score: 4.9
      },
      position: { x: 400, y: 1700 },
    },
    
    // Benefits Box
    {
      id: 'benefits',
      data: { label: '✨ Memory Benefits\n\n• 95% memory usage reduction\n• 100% memory leak elimination\n• 98% faster garbage collection\n• Zero application crashes\n• Predictive memory management' },
      position: { x: 800, y: 1650 },
      style: { ...themeStyles.uniqueFeatureNode, width: 250, height: 140 },
    }
  ];
};

const memoryEdges = [
  // Initial flow
  { id: 'e1', source: 'user-intent', target: 'memory-analysis', animated: true, style: themeStyles.edgeStyle },
  { id: 'e2', source: 'memory-analysis', target: 'initial-code', style: themeStyles.edgeStyle },
  { id: 'e3', source: 'initial-code', target: 'scoring-label', animated: true, style: themeStyles.edgeStyle },
  
  // Scoring connections
  { id: 'e4', source: 'scoring-label', target: 'heap-score', style: themeStyles.edgeStyle },
  { id: 'e5', source: 'scoring-label', target: 'gc-score', style: themeStyles.edgeStyle },
  { id: 'e6', source: 'scoring-label', target: 'leak-score', style: themeStyles.edgeStyle },
  { id: 'e7', source: 'scoring-label', target: 'stability-tests', style: themeStyles.edgeStyle },
  { id: 'e8', source: 'scoring-label', target: 'performance-impact', style: themeStyles.edgeStyle },
  
  // Genetic Algorithm
  { id: 'e9', source: 'initial-code', target: 'genetic-algo', style: themeStyles.evolutionEdgeStyle },
  { id: 'e10', source: 'genetic-algo', target: 'ai-agents', animated: true, style: themeStyles.evolutionEdgeStyle },
  
  // AI Agents
  { id: 'e11', source: 'ai-agents', target: 'leak-agent', style: themeStyles.edgeStyle },
  { id: 'e12', source: 'ai-agents', target: 'gc-agent', style: themeStyles.edgeStyle },
  { id: 'e13', source: 'ai-agents', target: 'allocation-agent', style: themeStyles.edgeStyle },
  
  // Generation 1
  { id: 'e14', source: 'leak-agent', target: 'gen1-1', style: themeStyles.edgeStyle },
  { id: 'e15', source: 'gc-agent', target: 'gen1-2', style: themeStyles.edgeStyle },
  { id: 'e16', source: 'allocation-agent', target: 'gen1-3', style: themeStyles.edgeStyle },
  { id: 'e17', source: 'ai-agents', target: 'gen1-more', style: { ...themeStyles.edgeStyle, strokeDasharray: '5 5' } },
  
  // Evolution to Gen 1
  { id: 'e18', source: 'gen1-1', target: 'evolution-1', style: themeStyles.evolutionEdgeStyle },
  { id: 'e19', source: 'gen1-2', target: 'evolution-1', animated: true, style: themeStyles.evolutionEdgeStyle },
  { id: 'e20', source: 'gen1-3', target: 'evolution-1', style: themeStyles.evolutionEdgeStyle },
  
  // Feedback loops
  { id: 'f1', source: 'heap-score', target: 'evolution-1', style: themeStyles.feedbackEdgeStyle, type: 'smoothstep' },
  { id: 'f2', source: 'stability-tests', target: 'evolution-1', style: themeStyles.feedbackEdgeStyle, type: 'smoothstep' },
  { id: 'f3', source: 'performance-impact', target: 'evolution-1', style: themeStyles.feedbackEdgeStyle, type: 'smoothstep' },
  
  // Generation 2
  { id: 'e21', source: 'evolution-1', target: 'gen2-1', animated: true, style: themeStyles.evolutionEdgeStyle },
  { id: 'e22', source: 'evolution-1', target: 'gen2-2', animated: true, style: themeStyles.evolutionEdgeStyle },
  
  // Final Evolution
  { id: 'e23', source: 'gen2-1', target: 'evolution-2', style: themeStyles.evolutionEdgeStyle },
  { id: 'e24', source: 'gen2-2', target: 'evolution-2', style: themeStyles.evolutionEdgeStyle },
  
  // Final Output
  { id: 'e25', source: 'evolution-2', target: 'final-output', animated: true, style: themeStyles.evolutionEdgeStyle },
  { id: 'e26', source: 'final-output', target: 'benefits', style: themeStyles.edgeStyle },
];

const ArtemisMemoryOptimization = () => {
  const [nodes, setNodes] = useState(getInitialNodes());
  const [edges, setEdges] = useState(memoryEdges);

  // Add custom styles on mount
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = customStyles;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Save positions whenever nodes change
  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => {
      const updatedNodes = applyNodeChanges(changes, nds);
      const positions = updatedNodes.map(node => ({
        ...node,
        style: node.style,
        type: node.type,
        data: node.data
      }));
      localStorage.setItem('artemisMemoryOptimizationPositions', JSON.stringify(positions));
      return updatedNodes;
    });
  }, []);

  const onEdgesChange = useCallback((changes) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
  }, []);

  // Reset positions
  const resetPositions = () => {
    localStorage.removeItem('artemisMemoryOptimizationPositions');
    setNodes(getInitialNodes());
  };

  return (
    <ReactFlowProvider>
      <div style={{ 
        height: '100vh', 
        width: '100%', 
        background: 'linear-gradient(135deg, #F0F4F8 0%, #D9E2EC 100%)',
        position: 'relative'
      }}>
        <h2 style={{ 
          textAlign: 'center', 
          color: '#102A43', 
          padding: '20px', 
          margin: '0',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          fontSize: '26px',
          fontWeight: '700'
        }}>
          💾 Artemis Memory Optimization - Evolving Efficiency
        </h2>
        
        <div style={{
          position: 'absolute',
          top: '70px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '8px 20px',
          background: 'rgba(16, 42, 67, 0.08)',
          borderRadius: '20px',
          color: '#486581',
          fontSize: '14px',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          zIndex: 100
        }}>
          Genetic algorithms evolve memory patterns for leak prevention and GC optimization
        </div>
        
        <button
          onClick={resetPositions}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            padding: '8px 16px',
            background: '#F4511E',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '12px',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
            zIndex: 100
          }}
        >
          Reset Layout
        </button>
        
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          style={{ height: 'calc(100vh - 120px)', marginTop: '120px' }}
        >
          <Background color="#aaa" gap={16} />
          <MiniMap 
            style={{
              background: 'white',
              border: '1px solid #ccc',
              borderRadius: '8px'
            }}
          />
          <Controls 
            style={{
              background: 'white',
              border: '1px solid #ccc',
              borderRadius: '8px'
            }}
          />
        </ReactFlow>
      </div>
    </ReactFlowProvider>
  );
};

export default ArtemisMemoryOptimization; 